import { useAppSelector, useAppTranslation } from "@/utils";
import { InstallmentsCalendarInstallmentInfo, MerchantInstallmentStatus } from "@payping/api/app/api/bnplMerchantTypes";
import { moment } from "@payping/utils/dateUtils";
import { Badge, Calender, Flex, Grid, Tag, Typography } from "@payping/web-components";
import { FC, useCallback, useMemo, useState } from "react";
import BnplFinancialCalenderPageCalenderDrawer from "./Drawer";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { selectAtom } from "jotai/utils";
import { dashboardPreferencesAtom } from "@/core/state";
import { useAtom } from "jotai";
import { LANGUAGES } from "@payping/utilities-shared/consts";

const languageAtom = selectAtom(dashboardPreferencesAtom, (val) => val.language);

interface CalenderProps {
  month: number;
  year: number;
  day: number;
}

export type BnplCalenderDataCell = Record<
  MerchantInstallmentStatus,
  { list: InstallmentsCalendarInstallmentInfo[]; totalAmount: number; number: number }
>;
const BnplFinancialCalenderPageCalender: FC<CalenderProps> = (props) => {
  const { t } = useAppTranslation();
  const installments = useAppSelector((state) => state.bnplMerchant.installmentCalendar)?.installments;
  const [language] = useAtom(languageAtom);

  const orderedInstallments = useMemo(() => {
    let temp: Record<string, BnplCalenderDataCell | undefined> = {};
    (installments || [])?.forEach((i) => {
      const date = moment(i.dueDate);
      const keyGenerated = `${date.year()}-${date.month()}-${date.date()}`;
      if (!Object.prototype.hasOwnProperty.call(temp, keyGenerated)) {
        temp = {
          ...temp,
          [keyGenerated]: {
            [MerchantInstallmentStatus.Delayed]: {
              list: [],
              number: 0,
              totalAmount: 0,
            },
            [MerchantInstallmentStatus.Due]: {
              list: [],
              number: 0,
              totalAmount: 0,
            },
            [MerchantInstallmentStatus.Paid]: {
              list: [],
              number: 0,
              totalAmount: 0,
            },
            [MerchantInstallmentStatus.SalaryDeduction]: {
              list: [],
              number: 0,
              totalAmount: 0,
            },
          },
        };
      }
      const list = temp[keyGenerated];
      if (!list) return;
      switch (i.status) {
        case MerchantInstallmentStatus.Delayed:
          list[MerchantInstallmentStatus.Delayed].list.push(i);
          list[MerchantInstallmentStatus.Delayed].number += 1;
          list[MerchantInstallmentStatus.Delayed].totalAmount += i.finalAmount ?? 0;
          return;
        case MerchantInstallmentStatus.SalaryDeduction:
          list[MerchantInstallmentStatus.Delayed].list.push(i);
          list[MerchantInstallmentStatus.Delayed].number += 1;
          list[MerchantInstallmentStatus.Delayed].totalAmount += i.finalAmount ?? 0;
          return;
        case MerchantInstallmentStatus.Due:
          list[MerchantInstallmentStatus.Due].list.push(i);
          list[MerchantInstallmentStatus.Due].number += 1;
          list[MerchantInstallmentStatus.Due].totalAmount += i.finalAmount ?? 0;
          return;
        default:
          list[MerchantInstallmentStatus.Paid].list.push(i);
          list[MerchantInstallmentStatus.Paid].number += 1;
          list[MerchantInstallmentStatus.Paid].totalAmount += i.finalAmount ?? 0;
      }
    });
    return temp;
  }, [installments]);

  const [openDrawer, setOpenDrawer] = useState<{
    data?: BnplCalenderDataCell;
    date?: moment.Moment;
    isOpen: boolean;
  }>({ isOpen: false, data: undefined });

  const { xxl } = Grid.useBreakpoint();

  const calenderValue = useMemo(() => {
    if (language === LANGUAGES.FARSI) {
      return moment(`${props.year}-${props.month + 1}-${props.day}`, "jYYYY-jM-jD");
    }
    return moment(`${props.year}-${props.month + 1}-${props.day}`, "YYYY-MM-MD");
  }, [language, props.day, props.month, props.year]);

  const handleCloseDrawer = () => {
    setOpenDrawer({ isOpen: false, data: undefined, date: undefined });
  };

  const OnSelectItem = (date: moment.Moment) => {
    const keyGenerated = `${date.year()}-${date.month()}-${date.date()}`;
    const foundItem = orderedInstallments[keyGenerated];
    setOpenDrawer({ isOpen: true, data: foundItem, date });
  };

  const cellRender = useCallback(
    (date: moment.Moment) => {
      const keyGenerated = `${date.year()}-${date.month()}-${date.date()}`;

      const foundItem = orderedInstallments[keyGenerated];

      if (!foundItem) {
        return null;
      }

      return (
        <Flex vertical gap={"small"}>
          {foundItem[MerchantInstallmentStatus.Paid].number ? (
            <Flex align="center" gap={"small"}>
              <Badge count={xxl ? foundItem[MerchantInstallmentStatus.Paid].number : undefined} color="green" />
              {xxl ? (
                <Tag color="green">
                  <Flex align="center">
                    <Typography.Text>
                      {formatLocalizedNumber({ value: foundItem[MerchantInstallmentStatus.Paid].totalAmount })}
                    </Typography.Text>
                    <CuToman width={24} height={24} />
                  </Flex>
                </Tag>
              ) : null}
            </Flex>
          ) : null}
          {foundItem[MerchantInstallmentStatus.Due].number ? (
            <Flex align="center" gap={"small"}>
              <Badge count={xxl ? foundItem[MerchantInstallmentStatus.Due].number : undefined} color="blue" />
              {xxl ? (
                <Tag color="blue">
                  <Flex align="center">
                    <Typography.Text>
                      {formatLocalizedNumber({ value: foundItem[MerchantInstallmentStatus.Due].totalAmount })}
                    </Typography.Text>
                    <CuToman width={24} height={24} />
                  </Flex>
                </Tag>
              ) : null}
            </Flex>
          ) : null}
          {foundItem[MerchantInstallmentStatus.Delayed].number ? (
            <Flex align="center" gap={"small"}>
              <Badge count={xxl ? foundItem[MerchantInstallmentStatus.Delayed].number : undefined} color="red" />
              {xxl ? (
                <Tag color="red">
                  <Flex align="center">
                    <Typography.Text>
                      {formatLocalizedNumber({ value: foundItem[MerchantInstallmentStatus.Delayed].totalAmount })}
                    </Typography.Text>
                    <CuToman width={24} height={24} />
                  </Flex>
                </Tag>
              ) : null}
            </Flex>
          ) : null}
        </Flex>
      );
    },
    [orderedInstallments, xxl, t],
  );

  return (
    <>
      <Calender headerRender={() => null} value={calenderValue} cellRender={cellRender} onSelect={OnSelectItem} />
      {openDrawer.isOpen ? (
        <BnplFinancialCalenderPageCalenderDrawer
          data={openDrawer.data}
          onClose={handleCloseDrawer}
          open={openDrawer.isOpen}
          date={openDrawer.date}
        />
      ) : null}
    </>
  );
};

export default BnplFinancialCalenderPageCalender;
